import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogOtherCostTypeData, CatalogOtherCostTypeDataFilterInput } from 'gql/graphql';
import { ProjectMethodologyEnum, SortEnumType } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { uercCalculationOtherCostTypesGql } from '../../../../UerProject/Calculation/CalculationOtherEdit/editOtherQueries';
import { bimContractOtherCostTypesGql, uerContractOtherCostTypesGql } from '../../OtherEdit/editOtherQueries';

interface IUseGetContractOtherCostTypes {
    methodology: ProjectMethodologyEnum;
    otherId?: number;
    contractId?: number;
    calculationId?: number;
    withEffect?: boolean;
}

const defaultVariables = {
    order: [
        {
            name: SortEnumType.Asc,
        },
    ],
    take: 50,
};

export const useGetContractOtherCostTypes = (props: IUseGetContractOtherCostTypes) => {
    const { methodology, otherId, contractId, calculationId, withEffect = true } = props;
    const dispatch = useDispatch();

    const [page, setPage] = useState(0);

    const requestId = useMemo(() => {
        const calculationRequestId =
            methodology === ProjectMethodologyEnum.Uer &&
            calculationId &&
            `uercCalculationOtherCostTypes${otherId || ''}`;
        const uerContractRequestId =
            methodology === ProjectMethodologyEnum.Uer && `uerContractOtherCostTypes${otherId || ''}`;

        return calculationRequestId || uerContractRequestId || `bimContractOtherCostTypes${otherId || ''}`;
    }, [methodology, calculationId, otherId]);

    const getEffect = useMemo(
        () => ({
            [ProjectMethodologyEnum.Uer]: Boolean(
                withEffect && (contractId || calculationId) && methodology === ProjectMethodologyEnum.Uer,
            ),
            [ProjectMethodologyEnum.Bim]: Boolean(
                withEffect && contractId && methodology === ProjectMethodologyEnum.Bim,
            ),
        }),
        [contractId, calculationId, methodology, withEffect],
    );

    const variables = useMemo(
        () => ({
            contractId,
            calculationId,
            contractOtherCostId: otherId,
            order: [{ name: SortEnumType.Asc }],
        }),
        [contractId, otherId, calculationId],
    );

    const [resetStoreData] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            resetStoreData(undefined);
        },
        [resetStoreData],
    );

    const [uerContractOtherCostTypesData, uerContractOtherCostTypesStatus, , uerContractOtherCostTypesMakeRequest] =
        useRequest({
            requestId: 'uerContractOtherCostTypes',
            query: uerContractOtherCostTypesGql,
            withEffect: getEffect[ProjectMethodologyEnum.Uer],
            variables,
        });

    const [bimContractOtherCostTypesData, bimContractOtherCostStatus, , bimContractOtherCostStatusMakeRequest] =
        useRequest({
            requestId: 'bimContractOtherCostTypes',
            query: bimContractOtherCostTypesGql,
            withEffect: getEffect[ProjectMethodologyEnum.Bim],
            variables,
        });

    const [calculationOtherCostTypesData, calculationOtherCostStatus, , calculationOtherCostTypesMakeRequest] =
        useRequest({
            requestId: 'uercCalculationOtherCostTypes',
            query: uercCalculationOtherCostTypesGql,
            withEffect: getEffect[ProjectMethodologyEnum.Uer] && !!calculationId,
            variables,
        });

    const otherCostTypesData = useMemo<{
        items: CatalogOtherCostTypeData[];
        status: RequestStatus | null;
        hasNextPage: boolean | undefined;
        request: Function | null;
    }>(() => {
        if (calculationId) {
            return {
                items:
                    calculationOtherCostTypesData?.uercCalculationOtherCostTypesQuery?.items ||
                    ([] as CatalogOtherCostTypeData[]),
                status: calculationOtherCostStatus,
                hasNextPage: calculationOtherCostTypesData?.uercCalculationOtherCostTypesQuery?.pageInfo?.hasNextPage,
                request: calculationOtherCostTypesMakeRequest,
            };
        }
        if (methodology === ProjectMethodologyEnum.Uer) {
            return {
                items:
                    uerContractOtherCostTypesData?.uerContractOtherCostTypes?.items ||
                    ([] as CatalogOtherCostTypeData[]),
                status: uerContractOtherCostTypesStatus,
                hasNextPage: uerContractOtherCostTypesData?.uerContractOtherCostTypes?.pageInfo?.hasNextPage,
                request: uerContractOtherCostTypesMakeRequest,
            };
        }

        return {
            items:
                bimContractOtherCostTypesData?.bimContractOtherCostTypes?.items || ([] as CatalogOtherCostTypeData[]),
            status: bimContractOtherCostStatus,
            hasNextPage: bimContractOtherCostTypesData?.bimContractOtherCostTypes?.pageInfo?.hasNextPage,
            request: bimContractOtherCostStatusMakeRequest,
        };
    }, [
        calculationOtherCostTypesData?.uercCalculationOtherCostTypesQuery?.items,
        calculationOtherCostStatus,
        calculationOtherCostTypesData?.uercCalculationOtherCostTypesQuery?.pageInfo?.hasNextPage,
        calculationOtherCostTypesMakeRequest,
        uerContractOtherCostTypesData?.uerContractOtherCostTypes?.items,
        uerContractOtherCostTypesStatus,
        uerContractOtherCostTypesData?.uerContractOtherCostTypes?.pageInfo?.hasNextPage,
        uerContractOtherCostTypesMakeRequest,
        bimContractOtherCostTypesData?.bimContractOtherCostTypes?.items,
        bimContractOtherCostStatus,
        bimContractOtherCostTypesData?.bimContractOtherCostTypes?.pageInfo?.hasNextPage,
        bimContractOtherCostStatusMakeRequest,
        methodology,
        calculationId,
    ]);

    const requestSearchByName = useCallback(
        (searchString?: string, currentPage = 1) => {
            if (otherCostTypesData.status === RequestStatus.FETCHING) {
                return;
            }

            setPage(currentPage + 1);

            const where: CatalogOtherCostTypeDataFilterInput | undefined = searchString
                ? {
                      name: { containsi: searchString },
                  }
                : undefined;

            const input = {
                ...variables,
                where,
                take: searchString ? undefined : defaultVariables.take,
                skip: searchString || currentPage === 1 ? 0 : currentPage * defaultVariables.take,
            };

            dispatch(otherCostTypesData.request?.(input));
        },
        [variables, dispatch, otherCostTypesData],
    );

    const onNextPageRequest = useCallback(() => {
        requestSearchByName('', page);
    }, [page, requestSearchByName]);

    return {
        items: otherCostTypesData.items,
        status: otherCostTypesData.status,
        onNextPageRequest,
        requestSearchByName,
        hasNextPage: otherCostTypesData.hasNextPage,
    };
};
